/**
 * True if the hostname is `withjoy.com` itself or a subdomain (e.g. `dev.withjoy.com`).
 * Rejects lookalike hosts such as `evil-withjoy.com` or `withjoy.net`.
 */
export const isJoyHost = (hostname: string): boolean => hostname === 'withjoy.com' || hostname.endsWith('.withjoy.com');

/**
 * True if the given URL is a `withjoy.com` URL (or subdomain).
 * Returns false for unparseable input or lookalike hosts.
 *
 * Used as the "only wrap joy URLs" gate at send-time in outbound messaging providers,
 * and as a pre-check in stationery URL heuristics.
 */
export const isJoyWebsiteUrl = (url: string): boolean => {
  try {
    return isJoyHost(new URL(url).hostname);
  } catch {
    return false;
  }
};

/**
 * True only if the URL is *this* event's guest site — `withjoy.com/{eventHandle}` or a page under
 * it. Distinct from `isJoyWebsiteUrl`, which is true of every `withjoy.com` URL there is.
 *
 * The distinction matters wherever a link is described from local event data instead of a crawl:
 * that substitution is only correct for the host's own site. Against the broader host check, a
 * registry link, a help-centre article, or another couple's site all pass — and each would then be
 * captioned with this event's name and photo.
 */
/**
 * Admin subtrees that sit under the event handle but are not the guest site. `/:eventHandle/edit`
 * and `/:eventHandle/workspace` are admin routes (see `main/router.tsx`), so they match the handle
 * while being login-walled pages no guest can open — describing one with the event's public title
 * and hero photo would let a host send guests a link they read as the wedding site and cannot use.
 */
const ADMIN_PATH_SEGMENTS: readonly string[] = ['edit', 'workspace'];

export const isEventGuestSiteUrl = (url: string, eventHandle: string | null | undefined): boolean => {
  if (!eventHandle) return false;
  try {
    const parsed = new URL(url);
    if (!isJoyHost(parsed.hostname)) return false;
    const [firstSegment, secondSegment] = parsed.pathname.split('/').filter(Boolean);
    // Handles are case-insensitive in URLs, so a shared link may not match the stored casing.
    if (!firstSegment || firstSegment.toLowerCase() !== eventHandle.toLowerCase()) return false;
    return !ADMIN_PATH_SEGMENTS.includes((secondSegment ?? '').toLowerCase());
  } catch {
    return false;
  }
};
